import {
  FormEvent,
  FC,
  useEffect,
  useState,
  ChangeEventHandler,
  useCallback,
} from 'react';

import NextLink from 'next/link';

import * as Yup from 'yup';
import { useTheme } from 'styled-components';
import { useToast } from '@chakra-ui/react';

import {
  getValidationErrors,
  ValidationErrors,
} from '~/shared/utils/getValidationErrors';
import { useSettings } from '~/shared/hooks/settings';
import { useModal } from '~/shared/hooks/modal';
import { routes } from '~/shared/constants/routes';
import { Input } from '~/shared/components/Form/Input';
import { FormGroup } from '~/shared/components/Form/FormGroup';
import { Button } from '~/shared/components/Form/Button';

import { useAuth } from '../../hooks/auth';
import { RedirectToResaleModal } from '../../components/RedirectToResaleModal';
import { Link } from '../../components/Link';
import { Header } from '../../components/Header';

interface FormData {
  email: string;
  password: string;
}

const signInSchema = Yup.object().shape({
  email: Yup.string().email('e-mail inválido').required('e-mail é obrigatório'),
  password: Yup.string().required('senha é obrigatória'),
});

export const SignIn: FC = () => {
  const { colors } = useTheme();
  const { settings } = useSettings();

  const { signIn, setPhoneNumber } = useAuth();

  const toast = useToast();
  const { openModal } = useModal();

  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState({} as ValidationErrors);

  const [isSigning, setIsSigning] = useState(false);

  useEffect(() => {
    setPhoneNumber('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeInput: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name, value } = event.target;

      if (formErrors[name]) {
        setFormErrors((prevState) => ({
          ...prevState,
          [name]: '',
        }));
      }

      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [formErrors]
  );

  async function handleSignIn(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();

    try {
      setIsSigning(true);

      const data = {
        ...formData,
        email: formData.email.trim(),
      };

      await signInSchema.validate(data, {
        abortEarly: false,
      });

      await signIn(data);
    } catch (error) {
      const tag = error?.response?.data?.tag;

      setIsSigning(false);

      if (tag === 'incorrect_resale_domain') {
        let domain = error?.response?.data?.content?.redirectToDomain as string;

        if (domain) {
          if (domain.includes('www')) {
            domain = domain.replace('www.', '');
          }

          openModal(RedirectToResaleModal, {
            modalProps: {
              closeOnClickOutside: false,
              closeOnEsc: false,
              shouldConfirm: false,
              showHeader: false,
            },
            props: {
              redirectToDomain: error.response.data.content.redirectToDomain,
            },
          });

          return;
        }
      }

      if (error instanceof Yup.ValidationError) {
        const validationErrors = getValidationErrors(error);

        setFormErrors((prevState) => ({
          ...prevState,
          ...validationErrors,
        }));

        return;
      }

      toast({
        status: 'error',
        isClosable: true,
        position: 'top-right',
        ...(tag === 'user_inactive'
          ? {
              title: error.response.data.message,
            }
          : {
              title: 'Ops, ocorreu um erro ao fazer o login!',
              description:
                'Parece que seu e-mail e/ou senha está(ão) incorreto(s).',
            }),
      });
    }
  }

  return (
    <>
      <form onSubmit={handleSignIn}>
        <Header
          title="Seja bem vindo_"
          subtitle={
            'Já é cliente? Faça login abaixo.\nNão tem senha? Use "esqueci a senha".'
          }
        />

        <FormGroup>
          <Input
            name="email"
            placeholder="E-mail"
            autoComplete="email"
            value={formData.email}
            error={formErrors.email}
            onChange={handleChangeInput}
            variant="default"
          />
        </FormGroup>

        <FormGroup>
          <Input
            name="password"
            autoComplete="current-password"
            type="password"
            placeholder="Senha"
            value={formData.password}
            error={formErrors.password}
            onChange={handleChangeInput}
            variant="default"
          />
        </FormGroup>

        <FormGroup>
          <Link href={routes.auth.forgotPassword} title="Esqueci a senha" />
        </FormGroup>

        <FormGroup>
          <Button type="submit" isLoading={isSigning}>
            Entrar
          </Button>

          {settings?.registrationPageLink && (
            <Button
              type="button"
              variant="outlined"
              backgroundColor="surface"
              borderColor="primary"
              textColor="primary"
            >
              <NextLink
                href={settings.registrationPageLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Ainda não sou cliente
              </NextLink>
            </Button>
          )}
        </FormGroup>
      </form>

      {settings?.helpLink && (
        <Link
          href={settings.helpLink}
          target="_blank"
          rel="noreferrer"
          customContent={
            <span style={{ color: colors.onSurface }}>
              Dúvida sobre o cadastro? <b>Clique aqui</b>
            </span>
          }
        />
      )}
    </>
  );
};
